import React, { useEffect, useState } from "react";
import Layout from "../../../components/Layout";
import Container from "../../../components/Container";
import DefaultBreadcrumbs from "../../../components/DefaultBreadcrumbs";
import Flex from "../../../components/Flex";
import { useAuthState } from "../../../components/AuthProvider";
import Content from "../../../components/Content";
import DataTable, {
  DropDownListFilter,
  SelectInputFilter
} from "../../../components/DataTable";
import ButtonList from "../../../components/ButtonList";
import LinkButton from "../../../components/LinkButton";
import { useHistory } from "react-router-dom";
import { useMessageToast } from "../../../components/MessageProvider";
import _groupBy from "lodash/groupBy";
import Modal from "../../../components/ModalPopup";
import Button from "../../../components/Button";
import { mergeArrayOfObjects } from "../../../utils/mergeArrayOfObjects";

import styles from "./Users.module.scss";
import Spinner from "../../../components/Loader/Spinner";

const Users = () => {
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const userRpcUrl = `${clientConfigApiDomain}/rpc/users`;
  const userApiUrl = `${clientConfigApiDomain}/users`;
  const rolesApiUrl = `${clientConfigApiDomain}/roles`;

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [userClientsFilter, setUserClientsFilter] = useState([]);

  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState({});

  const clickableCell = ({
    cell: {
      value,
      row: { original: selectedUser }
    }
  }) => {
    const onViewClick = () => {
      history.push(`/admin/users/${selectedUser.id}`, [users.id]);
    };

    return (
      <span className={styles.cell} onClick={onViewClick}>
        {selectedUser.last_name}, {selectedUser.first_name}
      </span>
    );
  };

  const handleDeleteSuccess = user => {
    toast.success(
      `Successfully deleted user: ${user.last_name}, ${user.first_name}.`
    );
    setUsers(users.filter(u => u !== user));
  };

  const handleDeleteError = user => {
    toast.error(
      `Error when attempting to delete user: ${selectedUser.last_name}, ${selectedUser.first_name}.`
    );
  };

  const onDeleteCancelClick = () => {
    setShowDeleteDialog(false);
    setSelectedUser({});
  };

  const onDeleteUser = () => {
    fetch(`${userApiUrl}/${selectedUser.id}/`, { method: "delete" })
      .then(response => {
        if (!response.ok) throw Error("Failed to delete user");
        handleDeleteSuccess(selectedUser);
      })
      .catch(() => {
        handleDeleteError(selectedUser);
      });
    setShowDeleteDialog(false);
  };

  const actionCell = ({
    cell: {
      value,
      row: { original: selectedUser }
    }
  }) => {
    const onDetailsClick = () => {
      history.push(`/admin/users/${selectedUser.id}`);
    };

    const onEditClick = () => {
      history.push(`/admin/users/${selectedUser.id}/edit`);
    };

    const onDeleteClick = () => {
      setSelectedUser(selectedUser);
      setShowDeleteDialog(true);
    };

    const isVisible = selectedUser => {
      const selectedUserRoles = [
        ...new Set(selectedUser.roles.map(role => role.name))
      ];
      return (
        user.role === "Global Admin" ||
        //!selectedUserRoles.includes("Global Admin") ||
        window.localStorage.getItem("LOGIN_TYPE") !== "MS-Email"
      );
    };

    const me = user.id === selectedUser.id;
    return (
      <ButtonList className={styles.icons}>
        <LinkButton icon="view" onClick={onDetailsClick}>
          Details
        </LinkButton>
        {isVisible(selectedUser) && (
          <LinkButton icon="edit" onClick={onEditClick}>
            Edit
          </LinkButton>
        )}
        {!me && isVisible(selectedUser) && (
          <LinkButton icon="remove" onClick={onDeleteClick}>
            Delete
          </LinkButton>
        )}
      </ButtonList>
    );
  };
  const userCsvColumns = [
    {
      Header: "Last Name",
      accessor: "last_name"
    },
    {
      Header: "First Name",
      accessor: "first_name"
    },
    {
      Header: "Email",
      accessor: "email"
    },
    {
      Header: "Role",
      accessor: "role"
    },
    {
      Header: "Organization or Account",
      accessor: "organization"
    }
  ];
  const userColumns = [
    {
      Header: "Name",
      accessor: row => row.last_name + row.first_name,
      Cell: clickableCell,
      sortType: (a, b) => {
        return `${a.values.last_name}, ${a.values.first_name}`.toLowerCase() >
          `${b.values.last_name}, ${b.values.first_name}`.toLowerCase()
          ? 1
          : -1;
      }
    },
    {
      Header: "Email",
      accessor: "email",
      sortType: (a, b) => {
        return a.values.email.toLowerCase() > b.values.email.toLowerCase()
          ? 1
          : -1;
      }
    },
    {
      Header: "Role",
      accessor: "role",
      Filter: DropDownListFilter,
      filter: "equals"
    },
    {
      Header: "Organization or Account",
      accessor: "organization",
      Filter: SelectInputFilter,
      filter: SelectInputFilter.filter,
      filterProps: { items: userClientsFilter }
    },
    {
      Header: "",
      accessor: "actions",
      disableFilters: true,
      disableSortBy: true,
      Cell: actionCell
    }
  ];

  useEffect(() => {
    const mapGetRoleNameOfUser = userRole => {
      return userRole.name;
    };
    const mapGetClientNameOfUser = userRole => {
      return userRole.name;
    };
    const mapUserRolesAndOrganization = user => {
      if (user.roles.length > 0) {
        user.role = [...new Set(user.roles.map(mapGetRoleNameOfUser))].join(
          ", "
        );
      } else {
        user.role = "No Access";
      }
      if (user.clients.length > 0) {
        user.organization = [
          ...new Set(user.clients.map(mapGetClientNameOfUser))
        ].join(", ");
      } else {
        user.organization = "No Access";
      }
    };
    const getUsers = async () => {
      //TODO: change this to use `?clients__organization__id=1` when working as a client admin.
      // This needs to be implemented on the backend also for RPC endpoint.
      if (user.role === "Organization Admin") {
        // This will fetch users for multiple org admin
        let fetchedUsers = [];
        for (const orgId of user.orgIds) {
          const users = await fetch(
            `${userRpcUrl}/?clients__organization__id=${orgId}`
          );
          const usersData = await users.json();
          fetchedUsers = mergeArrayOfObjects(
            fetchedUsers,
            usersData.results,
            "id"
          );
        }
        fetchedUsers.map(mapUserRolesAndOrganization);
        setUsers(fetchedUsers);
      } else {
        const response = await fetch(`${userRpcUrl}/`);
        const data = await response.json();
        if (data.hasOwnProperty("results")) {
          data.results.map(mapUserRolesAndOrganization);
          setUsers(data.results);
        }
      }
    };
    const createUserClientsOrganizationsFilter = () => {
      const userClientMapped = user.clients.map(client => ({
        client: client.name,
        organization: client.organization.name
      }));
      const userClientGroupByOrg = _groupBy(userClientMapped, `organization`);
      const userClient = Object.keys(userClientGroupByOrg).map(org => ({
        text: org,
        children: userClientGroupByOrg[org].map(childValues => ({
          text: childValues.client,
          value: childValues.client
        }))
      }));

      setUserClientsFilter(userClient);
    };
    if (!hasLoaded) {
      setIsLoading(true);
      getUsers();
      createUserClientsOrganizationsFilter();
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [
    setIsLoading,
    setHasLoaded,
    setUserClientsFilter,
    hasLoaded,
    userRpcUrl,
    rolesApiUrl,
    user.clients,
    user.role,
    user.orgIds
  ]);

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>
            <DefaultBreadcrumbs />
          </Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                <h1>Users</h1>
                <p>
                  Select an user to manage its respective properties and access.
                </p>
              </Content>
              <div className={styles.base}>
                <ButtonList spacing={2} style={{ margin: "1rem 0" }}>
                  <LinkButton
                    icon="add"
                    onClick={() => {
                      history.push("/admin/users/add");
                    }}
                    disabled={
                      user.role === "Organization Admin" &&
                      window.localStorage.getItem("LOGIN_TYPE") === "MS-Email"
                    }
                  >
                    Add User
                  </LinkButton>
                </ButtonList>
                <div>
                  <DataTable
                    columns={userColumns}
                    csvColumns={userCsvColumns}
                    data={users}
                    hasPagination
                    hasFiltering
                    hasSorting
                    hasDownloadCsv={true}
                  />
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
      <Modal open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <div style={{ marginBottom: "1rem" }}>
          Are you sure you want to delete user: {selectedUser.last_name},{" "}
          {selectedUser.first_name}?
        </div>
        <ButtonList>
          <Button primary type="submit" onClick={onDeleteUser}>
            Confirm
          </Button>
          <Button onClick={onDeleteCancelClick}>Cancel</Button>
        </ButtonList>
      </Modal>
    </Layout>
  );
};

export default Users;
